import { useSyncExternalStore } from 'react';
import type { DesktopBridge, DesktopState } from '@kiipu/desktop-protocol';
import { faviconContentPath } from './site-favicons';

let state: DesktopState | null = null;
const listeners = new Set<() => void>();
export const desktopBridge = (): DesktopBridge | null =>
  typeof window !== 'undefined' && window.kiipuDesktop?.version === 1 ? window.kiipuDesktop : null;
export const isDesktop = () => desktopBridge() !== null;
export const platformState = () => state;
export const canMutate = () => !isDesktop() || Boolean(state?.online && state.user);
export const subscribePlatform = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const usePlatform = () => useSyncExternalStore(subscribePlatform, platformState, () => null);
export function acceptPlatformState(value: DesktopState) {
  state = value;
  for (const listener of listeners) listener();
}
export function openSettings(path = isDesktop() ? '/settings/general' : '/settings/preferences') {
  const bridge = desktopBridge();
  if (bridge) return bridge.openSettings(path);
  window.location.assign(path);
}
export function openExternal(url: string) {
  const bridge = desktopBridge();
  if (bridge) return bridge.openExternal(url);
  window.location.assign(url);
}
export const readClipboardText = () =>
  desktopBridge()?.readClipboardText() ?? navigator.clipboard.readText();
export const writeClipboardText = (value: string) =>
  desktopBridge()?.writeClipboardText(value) ?? navigator.clipboard.writeText(value);

/** Private media accepts only an API-owned asset route; arbitrary custom-protocol URLs stay blocked. */
export function privateMediaUrl(value: string | null | undefined): string | undefined {
  if (!value) return;
  if (!isDesktop()) {
    try {
      const url = new URL(value, location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
    } catch {
      return;
    }
  }
  try {
    const origin = platformState()?.apiOrigin;
    const url = new URL(value, origin);
    if (origin) {
      const favicon = faviconContentPath(value, origin);
      if (favicon) return favicon;
    }
    if (
      url.origin !== origin ||
      !/^\/api\/v1\/assets\/[a-zA-Z0-9-]{1,100}\/content$/.test(url.pathname) ||
      url.hash
    )
      return;
    if ([...url.searchParams].some(([key, val]) => key !== 'thumbnail' || val !== '1')) return;
    return url.pathname + url.search;
  } catch {
    return;
  }
}
